import { Component, Mixins } from 'vue-property-decorator';
import { CssDevice, isMobileQuery, MagicConfigMixin } from '@gig/magic-common';
import { RouteName } from '../../routes/routes.constants';
import {
    BREAKPOINT_DEVICE_UPDATED,
    GAMEPLAY_BLOCK_LAUNCH_QUERY,
    GAMES_CURRENT_GAME_POSITION_UPDATED,
    GAMES_CURRENT_GAME_UPDATED,
    GAMES_SET_CURRENT_GAME_SLUG,
    USER_LOGGED_IN_UPDATED,
} from '@gig/magic-messages';
import {
    IChannelEvent,
    ISubscription,
} from '@gig/magic-message-bus/dist/base-bus';
import { IGame } from '@gig/magic-sdk/dist/adapter/games/game';

@Component
export default class Game extends Mixins(MagicConfigMixin) {
    RouteName = RouteName;

    game: IGame | null = null;

    private isMobile: boolean = false;
    private position: { x: number; y: number } | null = null;

    private breakpointSubscription: ISubscription | null = null;
    private loggedInSubscription: ISubscription | undefined;
    private gameSubscription: ISubscription | undefined;
    private gamePositionSubscription: ISubscription | undefined;

    get isMinimized() {
        return this.$route.name !== 'gameplay';
    }

    async onClose() {
        this.setCurrentGame(null);

        // otherwise, it cancels login page coming from a route change
        const blockLaunch = await window.queryBus.query<boolean>(
            GAMEPLAY_BLOCK_LAUNCH_QUERY,
        );
        if (!blockLaunch) {
            this.goBackToOriginUrl();
        }
    }

    private goBackToOriginUrl() {
        return this.$router.push('/');
    }

    created() {
        this.breakpointSubscription = window.eventBus.subscribe(
            BREAKPOINT_DEVICE_UPDATED,
            async (event: IChannelEvent<CssDevice>) => {
                if (!event.payload) {
                    return null;
                }
                this.isMobile = await isMobileQuery();
            },
            { replay: true },
        );

        this.loggedInSubscription = window.eventBus.subscribe<boolean>(
            USER_LOGGED_IN_UPDATED,
            () => {
                this.gamePositionChanged();
            },
        );

        this.gameSubscription = window.eventBus.subscribe<IGame | null>(
            GAMES_CURRENT_GAME_UPDATED,
            (event) => {
                this.game = event.payload!;
            },
            { replay: true },
        );

        this.gamePositionSubscription = window.eventBus.subscribe<{
            x: number;
            y: number;
        } | null>(
            GAMES_CURRENT_GAME_POSITION_UPDATED,
            (event) => {
                this.position = event.payload!;
                this.gamePositionChanged();
            },
            { replay: true },
        );
    }

    destroyed() {
        this.breakpointSubscription?.unsubscribe();
        this.loggedInSubscription?.unsubscribe();
        this.gameSubscription?.unsubscribe();
        this.gamePositionSubscription?.unsubscribe();
    }

    private gamePositionChanged() {
        const el = this.$el as HTMLElement;
        if (!this.game || !el) {
            return;
        }

        const top = this.position ? `${this.position.y}px` : 'auto';
        const left = this.position ? `${this.position.x}px` : 'auto';

        if (this.isMinimized || !this.position) {
            el.style.removeProperty('--magic-game-frame-position-top');
            el.style.removeProperty('--magic-game-frame-position-left');
        } else {
            el.style.setProperty('--magic-game-frame-position-top', top);
            el.style.setProperty('--magic-game-frame-position-left', left);
        }
    }

    private setCurrentGame(slug: string | null) {
        window.commandBus.publish(GAMES_SET_CURRENT_GAME_SLUG, slug);
    }
}
